import { ApolloError, isApolloError, ServerError } from '@apollo/client'
import { GraphQLFormattedError } from 'graphql'

import { ApolloOperationError } from './ApolloOperationError'

type TNetworkError = OmitSafe<ApolloError, 'networkError'> & {
  networkError: Defined<ApolloError['networkError']>
}

// Error has `result` property
const isServerError = (error: Error): error is ServerError => {
  return 'result' in error
}

/**
 * Error has `result.code === 'INVALID_TOKEN'`
 */
export const isAuthError = (error: Error) => {
  return (
    isServerError(error) &&
    typeof error.result !== 'string' &&
    error.result.code === 'INVALID_TOKEN'
  )
}

/**
 * Error is ApolloError and has `networkError` property
 */
export const isNetworkError = (error: Error): error is TNetworkError => {
  return Boolean(isApolloError(error) && error.networkError)
}

/**
 * Error message contains "Unexpected end of" or "Failed to fetch"
 */
export const isNetworkUnreachableError = (error: ApolloError) => {
  return Boolean(
    error.networkError &&
      (error.networkError.message === 'Failed to fetch' ||
        error.networkError.message.indexOf('Unexpected end of JSON input') >= 0 ||
        error.networkError.message.indexOf('unexpected end of data') >= 0),
  )
}

/**
 * Error message contains "Loading chunk" -> some JS or CSS chunk loading failed
 */
export const isChunkError = (error: Error) => {
  return Boolean(
    error.message &&
      (error.message.indexOf('Loading chunk') >= 0 ||
        error.message.indexOf('Loading CSS chunk') >= 0),
  )
}

/**
 * Error object has properties of the `ApolloOperationError` class
 */
export const isOperationError = (err: Error): err is ApolloOperationError => {
  return (
    // eslint-disable-next-line no-prototype-builtins
    err.hasOwnProperty('operationDoc') &&
    // eslint-disable-next-line no-prototype-builtins
    err.hasOwnProperty('operationVariables')
  )
}

/**
 * Query, mutation (or subscription?) returned some error from BE that should be presented to the user
 *
 * This function collects the error messages
 */
export const getUserErrors = (error: Error | ApolloError | RoA<GraphQLFormattedError>) => {
  if (error instanceof Error && !(error instanceof ApolloError)) return null

  const errors = error instanceof ApolloError ? error.graphQLErrors : error

  return errors.filter(shouldShowToUser).reduce((acc, err) => {
    return [...acc, err.message]
  }, [] as string[])
}

/**
 * Decides whether the error should be presented to the user
 */
export const shouldShowToUser = (error: GraphQLFormattedError) => {
  return Boolean(error.extensions && error.extensions.showToUser)
}

/**
 * Returns an `error` that should not be presented to the user
 */
export const doNotShowToUser = (error: GraphQLFormattedError) => !shouldShowToUser(error)
